import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@mantine/core';
import { IconArrowRight, IconChevronRight, IconMapPin } from '@tabler/icons-react';
import { RIDES_BOOKING_STATUS, type RidesAvailabilityView, type RidesBookingView, type RidesCampusView } from '@actschurch/ccms-sdk';
import { dateBlock, formatDate, formatShortDate, formatTime, plural } from '@/lib/format';
import { photoRules, useCopy } from '@/lib/settings';
import { Pill } from '@/components/rides/Pill';
import { PlateChip } from '@/components/rides/PlateChip';
import { PersonAvatar } from '@/components/rides/PersonAvatar';
import { bookingStatus, carWords, dayWord, isToday, liftStatus, meetingPoint, riderNames } from './trips';
import '@/styles/member.css';

function MeetingPoint({ campus }: { campus: RidesCampusView | undefined }) {
  const meet = meetingPoint(campus);
  if (!meet) return null;
  return (
    <div className="rm-meet">
      <span className="ic" aria-hidden="true"><IconMapPin size={22} /></span>
      <div style={{ minWidth: 0 }}>
        <span className="t">Where to meet</span>
        <p>{meet.text}</p>
        {meet.mapsUrl && (
          <a className="maps" href={meet.mapsUrl} target="_blank" rel="noopener noreferrer">
            Open in Maps<span className="sr-only"> (opens in a new tab)</span>
          </a>
        )}
      </div>
    </div>
  );
}

export interface NextRideCardProps {
  booking: RidesBookingView;
  /** Driver photo, already filtered by the visibility settings. */
  driverPhotoUrl: string | null;
  /** The pick-up campus (for "Where to meet"). */
  campus: RidesCampusView | undefined;
  /** False during maintenance: details stay readable, but the ride-day page is paused. */
  interactive?: boolean;
}

function TicketTop({ kicker, chip, today, title, date, time, timeTitle, timeText }: {
  kicker: string; chip: string; today: boolean; title: string; date: string; time: string; timeTitle: ReactNode; timeText: ReactNode;
}) {
  const word = dayWord(date);
  return (
    <div className="band">
      <div className="row1">
        <span className="kicker">{kicker}</span>
        <span className={`chip${today ? ' today' : ''}`}><span className="dot" aria-hidden="true" />{chip}</span>
      </div>
      <h2 id="rm-next-title">{title}</h2>
      <p className="date">{word && <b>{word}. </b>}{formatDate(date)}</p>
      <div className="timeline">
        <span className={`bigtime${time ? '' : ' tbc'}`}>{time || 'Time to be confirmed'}</span>
        <span className="what"><b>{timeTitle}</b>{timeText}</span>
      </div>
    </div>
  );
}

function Notch() {
  return <div className="notch" aria-hidden="true"><span /></div>;
}

/**
 * Home's "what's next" for a rider, shaped like a ticket: the coloured half says when (big
 * departure time), the white half says who and what to look for (photo, car, big number plate)
 * and where to meet.
 */
export function NextRideCard({ booking: b, driverPhotoUrl, campus, interactive = true }: NextRideCardProps) {
  const { t } = useCopy();
  const status = bookingStatus(b);
  const today = isToday(b.date);
  const time = formatTime(b.departureTime);
  const service = formatTime(b.serviceTimeStart);
  return (
    <section className="rm-ticket rm-rise" aria-labelledby="rm-next-title">
      <TicketTop
        kicker="Your next ride"
        chip={status.label === 'Confirmed' ? (today ? 'Today' : 'Confirmed') : status.label}
        today={today}
        title={b.eventName}
        date={b.date}
        time={time || service}
        timeTitle={time ? `Pick-up at ${b.collectionName}` : service ? `Service at ${b.dropOffName}` : `${b.collectionName} to ${b.dropOffName}`}
        timeText={time ? `Going to ${b.dropOffName}${service ? ` for the ${service} service` : ''}` : service ? `Pick-up at ${b.collectionName}` : 'Your driver will confirm the time.'}
      />
      <Notch />
      <div className="half">
        <div className="driver">
          <PersonAvatar src={driverPhotoUrl} name={b.driverName} size={60} decorative />
          <span className="who">
            <small>Your driver</small>
            <b>{b.driverName}</b>
            <span>{carWords(b.vehicle)}</span>
          </span>
        </div>
        {b.vehicle?.plate && (
          <div className="platebox">
            <small>Look for this number plate</small>
            <PlateChip plate={b.vehicle.plate} large />
          </div>
        )}
        <MeetingPoint campus={campus} />
        {today && <p className="note">{t('home.today_rider')}</p>}
        {interactive && (
          <Button className="go" fullWidth size="lg" component={Link} to={`/ride/day/${b.rideBookingID}`} rightSection={<IconArrowRight size={20} />}>
            {today ? "Open today's ride" : 'See ride details'}
          </Button>
        )}
      </div>
    </section>
  );
}

export interface NextLiftCardProps {
  listing: RidesAvailabilityView;
  campus: RidesCampusView | undefined;
  interactive?: boolean;
}

/** Home's "what's next" for a driver: when to leave, who has booked, the car riders look for and where they wait. */
export function NextLiftCard({ listing: l, campus, interactive = true }: NextLiftCardProps) {
  const { t, settings } = useCopy();
  const status = liftStatus(l);
  const today = isToday(l.date);
  const time = formatTime(l.departureTime);
  const service = formatTime(l.serviceTimeStart);
  const names = riderNames(l.bookings);
  const riders = (l.bookings ?? []).filter((x) => x.statusID === RIDES_BOOKING_STATUS.Confirmed).slice(0, 3);
  const showPhotos = photoRules(settings).riderToDrivers;
  return (
    <section className="rm-ticket rm-rise" aria-labelledby="rm-next-title">
      <TicketTop
        kicker="Your next lift"
        chip={today ? 'Today' : status.label}
        today={today}
        title={l.eventName}
        date={l.date}
        time={time || service}
        timeTitle={time ? `Leave ${l.collectionName}` : `Service at ${l.dropOffName}`}
        timeText={time ? `Driving to ${l.dropOffName}${service ? ` for the ${service} service` : ''}` : `Pick-up at ${l.collectionName}`}
      />
      <Notch />
      <div className="half">
        <div className="seats">
          {riders.length > 0 && (
            <span className="avs" aria-hidden="true">
              {riders.map((r) => (
                <PersonAvatar
                  key={r.rideBookingID}
                  src={showPhotos && !r.forMinor ? r.riderPhotoUrl : null}
                  name={r.forMinor ? r.childName || 'Child' : r.riderName}
                  size={40}
                  decorative
                />
              ))}
            </span>
          )}
          <span className="who" style={{ flex: 1, minWidth: 0 }}>
            <b style={{ display: 'block', fontSize: 18 }}>{l.seatsBooked === 0 ? 'Nobody has booked yet' : `${l.seatsBooked} of ${plural(l.seatsTotal, 'seat')} booked`}</b>
            <span style={{ display: 'block', color: 'var(--rides-muted)', fontSize: 15 }}>{names ? `Riding with you: ${names}` : 'Riders can book until shortly before you leave.'}</span>
          </span>
        </div>
        {l.vehicle?.plate && (
          <div className="platebox">
            <small>Riders will look for {carWords(l.vehicle) ? `your ${carWords(l.vehicle)}` : 'your car'}</small>
            <PlateChip plate={l.vehicle.plate} large />
          </div>
        )}
        <MeetingPoint campus={campus} />
        {today && <p className="note">{t('home.today_driver')}</p>}
        {interactive && (
          <Button className="go" fullWidth size="lg" component={Link} to={`/drive/day/${l.rideAvailabilityID}`} rightSection={<IconArrowRight size={20} />}>
            {today ? "Open today's lift" : 'See lift details'}
          </Button>
        )}
      </div>
    </section>
  );
}

/** A friendly scene for the "nothing booked yet" card (decorative, colours follow the theme). */
export function WelcomeArt() {
  return (
    <svg className="art" viewBox="0 0 340 130" role="presentation" aria-hidden="true" focusable="false">
      <circle className="rm-art-sun" cx="58" cy="34" r="18" />
      <path className="rm-art-hill" d="M0 92 C 60 58, 120 60, 180 84 S 290 110, 340 74 L 340 130 L 0 130 Z" />
      <path className="rm-art-hill2" d="M0 112 C 70 92, 150 96, 220 110 S 310 120, 340 108 L 340 130 L 0 130 Z" />
      <path className="rm-art-road" d="M18 120 C 90 96, 170 112, 236 94 S 300 70, 318 58" />
      <path className="rm-art-lane" d="M18 120 C 90 96, 170 112, 236 94 S 300 70, 318 58" />
      <g transform="translate(118 78)">
        <path className="rm-art-car" d="M4 22 L10 9 C 12 5, 15 3, 20 3 L 44 3 C 49 3, 52 5, 55 9 L 62 22 C 66 22, 68 24, 68 28 L 68 34 L 0 34 L 0 28 C 0 24, 1 22, 4 22 Z" />
        <path className="rm-art-glass" d="M14 21 L18 11 C 19 9, 20 8, 23 8 L 31 8 L 31 21 Z M 35 21 L 35 8 L 43 8 C 46 8, 47 9, 49 11 L 54 21 Z" />
        <circle className="rm-art-wheel" cx="16" cy="35" r="7" />
        <circle className="rm-art-hub" cx="16" cy="35" r="3" />
        <circle className="rm-art-wheel" cx="52" cy="35" r="7" />
        <circle className="rm-art-hub" cx="52" cy="35" r="3" />
      </g>
      <g transform="translate(296 18)">
        <path className="rm-art-pin" d="M14 0 C 22 0, 28 6, 28 14 C 28 24, 14 38, 14 38 C 14 38, 0 24, 0 14 C 0 6, 6 0, 14 0 Z" />
        <circle className="rm-art-pindot" cx="14" cy="14" r="5" />
      </g>
      <g transform="translate(250 62)">
        <circle className="rm-art-person" cx="7" cy="5" r="5" />
        <path className="rm-art-person" d="M0 24 C 0 15, 3 11, 7 11 C 11 11, 14 15, 14 24 Z" />
      </g>
    </svg>
  );
}

/** " at 09:00" (departure) or " for the 09:45 service" when only the service time is known. */
function whenWords(t: { departureTime?: string | null; serviceTimeStart?: string | null }): string {
  const dep = formatTime(t.departureTime);
  if (dep) return ` at ${dep}`;
  const svc = formatTime(t.serviceTimeStart);
  return svc ? ` for the ${svc} service` : '';
}

function RowShell({ to, past, date, title, detail, side }: { to?: string; past?: boolean; date: string; title: ReactNode; detail: ReactNode; side?: ReactNode }) {
  const tile = dateBlock(date);
  const inner = (
    <>
      <span className="rides-ev-date" aria-hidden="true">
        <span className="d" style={{ display: 'block' }}>{tile.d}</span>
        <span className="m" style={{ display: 'block' }}>{tile.m}</span>
      </span>
      <span className="txt">
        <span className="t">{title}</span>
        <span className="d">{detail}</span>
        {side && <span className="side">{side}</span>}
      </span>
      {to && <span className="chev" aria-hidden="true"><IconChevronRight size={22} /></span>}
    </>
  );
  const cls = `rm-row${past ? ' past' : ''}`;
  return to ? <Link to={to} className={cls}>{inner}</Link> : <div className={cls}>{inner}</div>;
}

/** An upcoming ride in the "Coming up" list. */
export function BookingRow({ booking: b, interactive = true }: { booking: RidesBookingView; interactive?: boolean }) {
  const status = bookingStatus(b);
  const when = whenWords(b);
  const notable = status.label !== 'Confirmed';
  return (
    <RowShell
      to={interactive ? `/ride/day/${b.rideBookingID}` : undefined}
      date={b.date}
      title={b.eventName}
      detail={<><span className="sr-only">{formatShortDate(b.date)}. </span>Ride from {b.collectionName}{when}</>}
      side={notable ? <Pill kind={status.tone}>{status.label}</Pill> : undefined}
    />
  );
}

/** An upcoming lift (the member is driving) in the "Coming up" list. */
export function LiftRow({ listing: l, interactive = true }: { listing: RidesAvailabilityView; interactive?: boolean }) {
  const when = whenWords(l);
  const full = l.seatsOpen === 0;
  return (
    <RowShell
      to={interactive ? `/drive/day/${l.rideAvailabilityID}` : undefined}
      date={l.date}
      title={l.eventName}
      detail={<><span className="sr-only">{formatShortDate(l.date)}. </span>You're driving from {l.collectionName}{when}. {l.seatsBooked} of {l.seatsTotal} booked.</>}
      side={full ? <Pill kind="ok">Full</Pill> : undefined}
    />
  );
}

/** A past (or cancelled) ride, for the short "Past rides" list. */
export function PastRow({ booking: b, interactive = true }: { booking: RidesBookingView; interactive?: boolean }) {
  const status = bookingStatus(b);
  return (
    <RowShell
      past
      to={interactive ? `/ride/day/${b.rideBookingID}` : undefined}
      date={b.date}
      title={b.eventName}
      detail={<><span className="sr-only">{formatShortDate(b.date)}. </span>With {b.driverName}</>}
      side={<Pill kind={status.tone}>{status.label}</Pill>}
    />
  );
}
